import React, { useState, useEffect } from 'react';
import { X, Building2, DollarSign, FileText, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import PhoneInput from './PhoneInput';
import FieldTooltip from './FieldTooltip';
import ValidationIndicator from './ValidationIndicator';
import { useAutoSave } from '../hooks/useAutoSave';
import { findSimilarCompanies, getSimilarityDescription } from '../utils/similarityUtils';
import { COUNTRIES, getStatesForCountry, hasStates } from '../utils/countryStateData';
import { 
  WORKDAY_INDUSTRIES, 
  COMPANY_FORMATION_TYPES, 
  COMPANY_STATUS_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  BILLING_FREQUENCY_OPTIONS,
  NET_TERMS_OPTIONS,
  CURRENCY_OPTIONS 
} from '../utils/companyData';

const CompanyFormComprehensive = ({ company, onClose, onSuccess, selectedUser }) => {
  const isEditMode = !!company;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    // ===== MANDATORY FIELDS =====
    client_name: '',
    status: 'Qualified Lead',
    company_formation_type: 'LLC',
    industry: '',
    
    // ===== PHONE NUMBERS (Standardized) =====
    main_phone: null,
    billing_phone: null,
    
    // ===== FINANCIAL & PLACEMENT =====
    standard_perm_fee: '',
    currency: 'USD',
    
    // ===== BILLING & INVOICING =====
    billing_contact: '',
    billing_frequency: 'Monthly',
    net_terms: 30,
    billing_email_id: '',
    
    // ===== OPTIONAL DETAILS =====
    parent_company: '',
    company_website: '',
    company_description: '',
    company_size: '',
    ein: '',
    location: 'United States',
    account_managers: [],
    
    // ===== ADDRESS =====
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    
    // ===== LEGACY COMPATIBILITY =====
    company_type: 'corporate',
    onboarded_by: 'system'
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accountManagerSearch, setAccountManagerSearch] = useState('');
  const [showAccountManagerDropdown, setShowAccountManagerDropdown] = useState(false);
  const [selectedAccountManagers, setSelectedAccountManagers] = useState([]);
  
  // UX Enhancement States
  const [duplicateCheck, setDuplicateCheck] = useState({ checking: false, isDuplicate: false, existingCompany: null });
  const [fieldValidations, setFieldValidations] = useState({});
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  
  // Auto-save integration
  const { loadDraft, clearDraft, lastSaved } = useAutoSave(
    `add-company-draft-${isEditMode ? company?.id : 'new'}`, 
    formData,
    !isEditMode // Only auto-save for new companies, not edits
  );

  // Check for duplicate company name with fuzzy matching
  const checkDuplicateName = async (companyName) => {
    if (!companyName || companyName.length < 3) {
      setDuplicateCheck({ checking: false, isDuplicate: false, existingCompany: null, similarCompanies: [] });
      return;
    }
    
    setDuplicateCheck({ checking: true, isDuplicate: false, existingCompany: null, similarCompanies: [] });
    
    try {
      const response = await fetch(`${backendUrl}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        const allCompanies = data.customers || [];
        
        // Filter out current company if editing
        const otherCompanies = allCompanies.filter(c => c.id !== company?.id);
        
        // Find similar companies using fuzzy matching
        const similarResults = findSimilarCompanies(companyName, otherCompanies, 70);
        
        if (similarResults.length > 0) {
          const topMatch = similarResults[0];
          setDuplicateCheck({ 
            checking: false, 
            isDuplicate: topMatch.score === 100, // Exact match
            existingCompany: topMatch.company,
            similarCompanies: similarResults.slice(0, 3), // Show top 3 similar
            topSimilarity: topMatch.score
          });
        } else {
          setDuplicateCheck({ checking: false, isDuplicate: false, existingCompany: null, similarCompanies: [] });
        }
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      setDuplicateCheck({ checking: false, isDuplicate: false, existingCompany: null, similarCompanies: [] });
    }
  };
  
  // Debounced duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isEditMode) {
        checkDuplicateName(formData.client_name);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.client_name, isEditMode]);
  
  // Smart Defaults: Currency based on Country
  useEffect(() => {
    if (formData.country === 'United Kingdom') {
      setFormData(prev => ({ ...prev, currency: 'GBP' }));
    } else if (formData.country === 'Canada') {
      setFormData(prev => ({ ...prev, currency: 'CAD' }));
    } else if (formData.country === 'India') {
      setFormData(prev => ({ ...prev, currency: 'INR' }));
    } else if (formData.country === 'Japan') {
      setFormData(prev => ({ ...prev, currency: 'JPY' }));
    } else if (formData.country === 'China') {
      setFormData(prev => ({ ...prev, currency: 'CNY' }));
    } else if (formData.country === 'Australia') {
      setFormData(prev => ({ ...prev, currency: 'AUD' }));
    } else if (formData.country === 'Mexico') {
      setFormData(prev => ({ ...prev, currency: 'MXN' }));
    } else if (formData.country.includes('Euro') || ['Germany', 'France', 'Italy', 'Spain', 'Netherlands'].includes(formData.country)) {
      setFormData(prev => ({ ...prev, currency: 'EUR' }));
    }
  }, [formData.country]);
  
  // Load draft on mount
  useEffect(() => {
    if (!isEditMode) {
      const draft = loadDraft();
      if (draft && draft.client_name) {
        setShowDraftPrompt(true);
      }
    }
  }, [isEditMode]);

  // Fetch available users for account managers
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/users`);
        if (response.ok) {
          const data = await response.json();
          setAvailableUsers(data.users || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, [backendUrl]);

  // Fetch companies for parent company dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/customers`);
        if (response.ok) {
          const data = await response.json();
          setCompanies(data.customers || []);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, [backendUrl]);

  // Populate form when editing
  useEffect(() => {
    if (company) {
      setFormData({
        client_name: company.client_name || '',
        status: company.status || 'Qualified Lead',
        company_formation_type: company.company_formation_type || company.ownership_type || 'LLC',
        industry: company.industry || '',
        main_phone: company.main_phone || null,
        billing_phone: company.billing_phone || null,
        standard_perm_fee: company.standard_perm_fee || '',
        currency: company.currency || company.billing_currency || 'USD',
        billing_contact: company.billing_contact || company.billing_contact_name || '',
        billing_frequency: company.billing_frequency || company.invoicing_frequency || 'Monthly',
        net_terms: company.net_terms || 30,
        billing_email_id: company.billing_email_id || '',
        parent_company: company.parent_company || '',
        company_website: company.company_website || '',
        company_description: company.company_description || company.company_overview || '',
        company_size: company.company_size || '',
        ein: company.ein || company.fein || '',
        location: company.location || company.country || 'United States',
        account_managers: company.account_managers || [],
        address_line_1: company.address_line_1 || '',
        address_line_2: company.address_line_2 || '',
        city: company.city || '',
        state: company.state || '',
        zip_code: company.zip_code || '',
        country: company.country || 'United States',
        company_type: company.company_type || 'corporate',
        onboarded_by: company.onboarded_by || 'system'
      });
      
      // Populate selected account managers for display
      if (company.account_managers && company.account_managers.length > 0 && availableUsers.length > 0) {
        const managers = availableUsers.filter(u => company.account_managers.includes(u.id));
        setSelectedAccountManagers(managers);
      }
    }
  }, [company, availableUsers]);

  const validate = () => {
    const newErrors = {};
    
    // Mandatory validations
    if (!formData.client_name || formData.client_name.length < 1 || formData.client_name.length > 200) {
      newErrors.client_name = 'Company name is required (1-200 characters)';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    if (!formData.company_formation_type) {
      newErrors.company_formation_type = 'Company formation type is required';
    }
    
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }
    
    if (!formData.address_line_1) {
      newErrors.address_line_1 = 'Address is required';
    }
    
    if (!formData.city) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.zip_code) {
      newErrors.zip_code = 'Zip code is required';
    }
    
    // Website validation
    if (formData.company_website && !/(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)/.test(formData.company_website)) {
      newErrors.company_website = 'Please enter a valid URL';
    }
    
    // Standard perm fee validation
    if (formData.standard_perm_fee && (parseFloat(formData.standard_perm_fee) < 0 || parseFloat(formData.standard_perm_fee) > 100)) {
      newErrors.standard_perm_fee = 'Fee must be between 0 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data
      const submitData = {
        ...formData,
        company_overview: formData.company_description,
        billing_contact_name: formData.billing_contact,
        billing_currency: formData.currency,
        payment_frequency: `Net ${formData.net_terms}`,
        invoicing_frequency: formData.billing_frequency,
        // Convert empty email strings to null for backend validation
        billing_email_id: formData.billing_email_id && formData.billing_email_id.trim() ? formData.billing_email_id : null,
        primary_contact_email: formData.primary_contact_email && formData.primary_contact_email.trim() ? formData.primary_contact_email : null,
        // Convert empty strings to null for optional numeric fields
        standard_perm_fee: formData.standard_perm_fee === '' ? null : formData.standard_perm_fee,
        // CRITICAL: Track who created this company
        created_by: selectedUser?.id || null,
        onboarded_by: selectedUser?.id || 'system'
      };
      
      const url = isEditMode 
        ? `${backendUrl}/api/customers/${company.id}`
        : `${backendUrl}/api/customers`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      let result;
      
      // Clone the response so we can read it twice if needed
      const responseClone = response.clone();
      
      try {
        result = await response.json();
      } catch (jsonError) {
        // Response is not valid JSON - might be HTML error page
        // Use the cloned response to read as text
        const text = await responseClone.text();
        console.error('Invalid JSON response:', text.substring(0, 200));
        throw new Error(`Server returned invalid response: ${response.status} ${response.statusText}`);
      }

      if (response.ok || result.success) {
        // Clear draft on successful submission
        clearDraft();
        toast.success(isEditMode ? 'Company updated successfully!' : 'Company created successfully!');
        onSuccess();
        onClose(); // Close modal after success
      } else {
        // Handle validation errors properly with field names
        let errorMessages = [];
        
        if (result.detail) {
          if (typeof result.detail === 'string') {
            errorMessages.push(result.detail);
          } else if (Array.isArray(result.detail)) {
            // Pydantic validation errors - extract field names and messages
            result.detail.forEach(err => {
              const fieldPath = err.loc ? err.loc.join(' -> ') : 'unknown field';
              const fieldName = err.loc && err.loc.length > 0 ? err.loc[err.loc.length - 1] : 'unknown';
              const message = err.msg || 'Invalid value';
              const inputValue = err.input !== undefined ? ` (got: ${JSON.stringify(err.input)})` : '';
              
              // Create user-friendly field names
              const friendlyFieldName = fieldName
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
              
              errorMessages.push(`❌ ${friendlyFieldName}: ${message}${inputValue}`);
            });
          } else if (typeof result.detail === 'object') {
            errorMessages.push(JSON.stringify(result.detail));
          }
        } else if (result.error) {
          errorMessages.push(typeof result.error === 'string' ? result.error : JSON.stringify(result.error));
        }
        
        // Show each error on a separate line
        if (errorMessages.length > 0) {
          const errorHtml = errorMessages.join('\n\n');
          toast.error(errorHtml, {
            duration: 8000, // Show longer for multiple errors
            style: {
              whiteSpace: 'pre-line',
              maxWidth: '600px'
            }
          });
        } else {
          toast.error('Failed to save company. Please check all fields.');
        }
      }
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Error saving company: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-6xl my-8">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-lg flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">{isEditMode ? 'Edit Company' : 'Add New Company'}</h2>
              <div className="flex items-center gap-3">
                <p className="text-blue-100 text-sm">Fields marked with * are required</p>
                {!isEditMode && lastSaved && (
                  <p className="text-blue-200 text-xs flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Auto-saved
                  </p>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Draft Restore Prompt */}
        {showDraftPrompt && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">You have an unsaved draft from earlier</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const draft = loadDraft();
                  if (draft) {
                    setFormData(draft);
                    toast.success('Draft restored!');
                  }
                  setShowDraftPrompt(false);
                }}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
              >
                Restore Draft
              </button>
              <button
                type="button"
                onClick={() => {
                  clearDraft();
                  setShowDraftPrompt(false);
                  toast.success('Draft discarded');
                }}
                className="px-3 py-1 bg-white text-gray-700 text-sm rounded border border-gray-300 hover:bg-gray-50"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-150px)] overflow-y-auto">
          
          {/* SECTION 1: Company Identity & Information */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Identity & Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    maxLength={200}
                    placeholder="Enter company name"
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.client_name || duplicateCheck.isDuplicate ? 'border-red-500' : 
                      formData.client_name.length >= 3 && !duplicateCheck.isDuplicate ? 'border-green-500' :
                      'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator 
                    isValid={formData.client_name.length >= 3 && !duplicateCheck.isDuplicate && !duplicateCheck.checking}
                    isInvalid={duplicateCheck.isDuplicate}
                  />
                </div>
                {duplicateCheck.checking && (
                  <p className="text-blue-500 text-xs mt-1">Checking for duplicates...</p>
                )}
                {(duplicateCheck.isDuplicate || (duplicateCheck.similarCompanies && duplicateCheck.similarCompanies.length > 0)) && (
                  <div className={`mt-2 p-3 border rounded ${
                    duplicateCheck.isDuplicate ? 'bg-orange-50 border-orange-300' : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <p className={`font-medium text-sm ${duplicateCheck.isDuplicate ? 'text-orange-800' : 'text-yellow-800'}`}>
                      {duplicateCheck.isDuplicate ? '⚠️ Matching Record Found' : '⚠️ Similar Companies Found'}
                    </p>
                    
                    {duplicateCheck.isDuplicate && duplicateCheck.existingCompany && (
                      <div className="mt-2 p-2 bg-white border border-orange-200 rounded">
                        <p className="text-xs text-gray-700"><strong>Company:</strong> {duplicateCheck.existingCompany.client_name}</p>
                        <p className="text-xs text-gray-700"><strong>Industry:</strong> {duplicateCheck.existingCompany.industry}</p>
                        {duplicateCheck.existingCompany.address_line_1 && (
                          <p className="text-xs text-gray-700"><strong>Address:</strong> {duplicateCheck.existingCompany.address_line_1}, {duplicateCheck.existingCompany.city}</p>
                        )}
                        <p className="text-xs text-orange-700 mt-2 font-medium">
                          ⚠️ A company with this name already exists. Are you sure this is a different company?
                        </p>
                      </div>
                    )}
                    
                    {!duplicateCheck.isDuplicate && duplicateCheck.similarCompanies && duplicateCheck.similarCompanies.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {duplicateCheck.similarCompanies.map((result, idx) => (
                          <div key={idx} className="p-2 bg-white border border-yellow-200 rounded">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-800">
                                {result.company.client_name || result.company.company_name}
                              </span>
                              <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">
                                {result.score}% match
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">Industry: {result.company.industry}</p>
                          </div>
                        ))}
                        <p className="text-yellow-700 text-xs mt-2">
                          Please verify you're not creating a duplicate. You can still proceed if this is a different company.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {errors.client_name && <p className="text-red-500 text-xs mt-1">{errors.client_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COMPANY_STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Formation Type <span className="text-red-500">*</span>
                  <FieldTooltip content="Legal entity type from W9 form. Check your incorporation documents or ask your accountant." />
                </label>
                <select
                  required
                  value={formData.company_formation_type}
                  onChange={(e) => setFormData({...formData, company_formation_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COMPANY_FORMATION_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">W9 entity type</p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry / Sector <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.industry ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select industry from Workday taxonomy...</option>
                  {WORKDAY_INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
                {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                <p className="text-xs text-gray-500 mt-1">Workday-aligned industry classification</p>
              </div>
              
              {/* Parent Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parent Company</label>
                <select
                  value={formData.parent_company}
                  onChange={(e) => setFormData({...formData, parent_company: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">None (Independent Company)</option>
                  {companies.map(comp => (
                    <option key={comp.id} value={comp.client_name || comp.company_name}>
                      {comp.client_name || comp.company_name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Company Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Website</label>
                <input
                  type="url"
                  value={formData.company_website}
                  onChange={(e) => setFormData({...formData, company_website: e.target.value})}
                  placeholder="https://company.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.company_website ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.company_website && <p className="text-red-500 text-xs mt-1">{errors.company_website}</p>}
              </div>
              
              {/* Company Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                <select
                  value={formData.company_size}
                  onChange={(e) => setFormData({...formData, company_size: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select company size...</option>
                  {COMPANY_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              
              {/* EIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  EIN (Employer Identification Number)
                  <FieldTooltip content="Found on IRS Form CP 575 or SS-4. Format: XX-XXXXXXX" />
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.ein}
                    onChange={(e) => setFormData({...formData, ein: e.target.value})}
                    placeholder="XX-XXXXXXX"
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formData.ein && /^\d{2}-\d{7}$/.test(formData.ein) ? 'border-green-500' : 'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator 
                    isValid={formData.ein && /^\d{2}-\d{7}$/.test(formData.ein)}
                    successMessage="Valid"
                  />
                </div>
              </div>
              
              {/* Company Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Description</label>
                <textarea
                  value={formData.company_description}
                  onChange={(e) => setFormData({...formData, company_description: e.target.value})}
                  rows={4}
                  placeholder="Brief description of the company (text only, no images/logos)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Account Managers - Searchable */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Manager(s)</label>
                
                {selectedAccountManagers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedAccountManagers.map(manager => (
                      <div key={manager.id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full inline-flex items-center gap-2 text-sm">
                        <span>{manager.full_name || manager.username}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAccountManagers(prev => prev.filter(m => m.id !== manager.id));
                            setFormData({
                              ...formData, 
                              account_managers: formData.account_managers.filter(id => id !== manager.id)
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Type to search account managers..."
                    value={accountManagerSearch}
                    onChange={(e) => {
                      setAccountManagerSearch(e.target.value);
                      setShowAccountManagerDropdown(true);
                    }}
                    onFocus={() => setShowAccountManagerDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  
                  {showAccountManagerDropdown && accountManagerSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {availableUsers
                        .filter(u => 
                          (u.role === 'account_manager' || u.role === 'account_exec' || u.role === 'admin') &&
                          !selectedAccountManagers.some(sm => sm.id === u.id) &&
                          ((u.full_name || '').toLowerCase().includes(accountManagerSearch.toLowerCase()) ||
                           (u.username || '').toLowerCase().includes(accountManagerSearch.toLowerCase()) ||
                           (u.email || '').toLowerCase().includes(accountManagerSearch.toLowerCase()))
                        )
                        .slice(0, 10)
                        .map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => {
                              setSelectedAccountManagers(prev => [...prev, user]);
                              setFormData({
                                ...formData,
                                account_managers: [...formData.account_managers, user.id]
                              });
                              setAccountManagerSearch('');
                              setShowAccountManagerDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-0"
                          >
                            <div className="font-medium text-gray-900">{user.full_name || user.username}</div>
                            <div className="text-sm text-gray-500">{user.email} • {user.role}</div>
                          </button>
                        ))}
                      
                      {availableUsers.filter(u => 
                        (u.role === 'account_manager' || u.role === 'account_exec' || u.role === 'admin') &&
                        !selectedAccountManagers.some(sm => sm.id === u.id) &&
                        ((u.full_name || '').toLowerCase().includes(accountManagerSearch.toLowerCase()) ||
                         (u.username || '').toLowerCase().includes(accountManagerSearch.toLowerCase()) ||
                         (u.email || '').toLowerCase().includes(accountManagerSearch.toLowerCase()))
                      ).length === 0 && (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          No matching account managers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Type to search and select sales managers or admins as account managers</p>
              </div>
            </div>
          </div>

          {/* SECTION 2: Contact & Address */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PhoneInput
                value={formData.main_phone}
                onChange={(phone) => setFormData({...formData, main_phone: phone})}
                label="Main Company Phone"
                placeholder="Main phone number"
              />
            </div>
          </div>

          {/* SECTION 3: Company Location & Address - OPTION 1: TRADITIONAL TOP-TO-BOTTOM */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Company Location</h3>
            <div className="space-y-4">
              {/* Address Lines */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.address_line_1}
                  onChange={(e) => setFormData({...formData, address_line_1: e.target.value})}
                  placeholder="Street address"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.address_line_1 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.address_line_1 && <p className="text-red-500 text-xs mt-1">{errors.address_line_1}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                <input
                  type="text"
                  value={formData.address_line_2}
                  onChange={(e) => setFormData({...formData, address_line_2: e.target.value})}
                  placeholder="Suite, floor, building (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* City - Simple Text Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>
              
              {/* State/Province - Smart Dropdown based on Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State/Province <span className="text-red-500">*</span>
                </label>
                {hasStates(formData.country) ? (
                  <select
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.state ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select {formData.country === 'United States' ? 'state' : 'province'}...</option>
                    {getStatesForCountry(formData.country).map(state => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    required
                    placeholder="Enter state/province"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.state ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}
                {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
              </div>
              
              {/* Zip/Postal Code - Simple Text Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zip/Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  placeholder="Enter zip code"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.zip_code ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.zip_code && <p className="text-red-500 text-xs mt-1">{errors.zip_code}</p>}
              </div>
              
              {/* Country - Dropdown (Determines State options) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value, state: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Changing country will reset state selection</p>
              </div>
            </div>
          </div>

          {/* SECTION 4: Financial & Placement Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Financial & Placement Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Standard Perm Fee (%)</label>
                <input
                  type="number"
                  value={formData.standard_perm_fee}
                  onChange={(e) => setFormData({...formData, standard_perm_fee: e.target.value})}
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="e.g., 20"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.standard_perm_fee ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.standard_perm_fee && <p className="text-red-500 text-xs mt-1">{errors.standard_perm_fee}</p>}
                <p className="text-xs text-gray-500 mt-1">Max 100%</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CURRENCY_OPTIONS.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name} ({curr.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 5: Billing & Invoicing */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Billing & Invoicing Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Contact</label>
                <input
                  type="text"
                  value={formData.billing_contact}
                  onChange={(e) => setFormData({...formData, billing_contact: e.target.value})}
                  placeholder="Name of billing contact person"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Free text, not linked to contact record</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Email</label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.billing_email_id}
                    onChange={(e) => setFormData({...formData, billing_email_id: e.target.value})}
                    placeholder="billing@company.com"
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formData.billing_email_id && /\S+@\S+\.\S+/.test(formData.billing_email_id) ? 'border-green-500' : 'border-gray-300'
                    }`}
                  />
                  <ValidationIndicator 
                    isValid={formData.billing_email_id && /\S+@\S+\.\S+/.test(formData.billing_email_id)}
                  />
                </div>
              </div>
              
              <div className="md:col-span-2">
                <PhoneInput
                  value={formData.billing_phone}
                  onChange={(phone) => setFormData({...formData, billing_phone: phone})}
                  label="Billing Phone Number"
                  placeholder="Billing department phone"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Frequency</label>
                <select
                  value={formData.billing_frequency}
                  onChange={(e) => setFormData({...formData, billing_frequency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {BILLING_FREQUENCY_OPTIONS.map(freq => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Net Terms
                  <FieldTooltip content="Payment terms. Net 30 = payment due in 30 days. Most common is Net 30." />
                </label>
                <select
                  value={formData.net_terms}
                  onChange={(e) => setFormData({...formData, net_terms: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {NET_TERMS_OPTIONS.map(term => (
                    <option key={term.value} value={term.value}>{term.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
            >
              {submitting ? (isEditMode ? 'Updating Company...' : 'Creating Company...') : (isEditMode ? 'Update Company' : 'Create Company')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyFormComprehensive;
